import React, { memo, FC, useState, useLayoutEffect, useCallback } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { useAuth } from '@sportworld/js-sdk/src/context/authContext'

import { Button } from '@/components/Button/Button'
import { IconButton } from '@/components/IconButton'
import { focusIds, orientation } from '@/constants/focus'
import { defaultBlockedBgImage, icons } from '@/constants/icons'
import { LoginSessionType } from '@/constants/login'
import { paths } from '@/constants/paths'
import { BodyText } from '@/containers/styles'
import { usePageRefresher } from '@/hooks/usePageRefresher'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import {
  getParentContainerFocusId,
  sendPageImpressionToGTAG,
} from '@/utils/utils'

import {
  BackBox,
  ButtonsBox,
  ControlsContainer,
  BlockedOverlayContainer,
  BlockedBgImage,
} from './styles'

const BlockedOverlayRaw: FC<{
  domRect?: DOMRect
  isFullscreen: boolean
  focusIdPrefix?: string
  premiumLockedscreenImage?: string
  showBlockedBgImage?: boolean
  disabled?: boolean
  search?: string
  isRequiresRegistrationOnly?: boolean
}> = ({
  isFullscreen,
  focusIdPrefix = '',
  premiumLockedscreenImage,
  showBlockedBgImage = true,
  disabled = false,
  search,
  isRequiresRegistrationOnly,
}) => {
  const { t } = useCommonTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const setAuthPopup = useStore((state) => state.common.setAuthPopup)
  const currentEvent = useStore((state) => state.common.currentEvent)
  const { pagePopup } = useStore((state) => state.popup)
  const [defaultFocusChild, setDefaultFocusChild] = useState(0)
  const { setIsFullscreen } = useStore((state) => state.player)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [blockedBgImage, setBlockedBgImage] = useState('')
  const setMediaVideo = useStore((state) => state.common.setMediaVideo)

  const { pageRefresh } = usePageRefresher()
  const { isRegisteredUser } = useAuth()

  const onMove = (e: MoveEvent) => {
    setDefaultFocusChild(Number(e.nextChildIndex))
  }

  useLayoutEffect(() => {
    setIsImageLoaded(false)
    setBlockedBgImage(premiumLockedscreenImage || defaultBlockedBgImage)
  }, [premiumLockedscreenImage])

  const sendEventToGTAG = useCallback(() => {
    const path = currentEvent
      ? `${paths.EVENT_PAGE}/${currentEvent.id}`
      : pagePopup
      ? `${paths.PAGE}/${pagePopup.id}`
      : pathname
    sendPageImpressionToGTAG(path)
  }, [pagePopup, currentEvent, pathname])

  return (
    <BlockedOverlayContainer>
      <ControlsContainer
        focusId={focusIdPrefix + focusIds.VIDEO_CONTAINER}
        elementType="div"
        orientation={orientation.VERTICAL}
        defaultFocusChild={defaultFocusChild}
        onMove={onMove}
        disabled={disabled}
        $transparent={disabled}
      >
        {showBlockedBgImage && (
          <BlockedBgImage
            src={blockedBgImage}
            className={isImageLoaded ? 'isVisible' : ''}
            onLoad={() => {
              setIsImageLoaded(true)
            }}
            onError={() => {
              setBlockedBgImage(defaultBlockedBgImage)
            }}
            alt=""
          />
        )}
        {!disabled && (
          <ButtonsBox $height={isFullscreen ? 150 : 52}>
            {isRegisteredUser ? (
              <Button
                iconSrc={icons.navigation.idCard}
                text={t('unlockPremium')}
                onClick={(e: LRUDEvent) => {
                  e.stopPropagation()
                  navigate(`${paths.SETTINGS}/${paths.SETTINGS_SUBSCRIPTIONS}`)
                  setMediaVideo(null)
                }}
                smallButton={!isFullscreen}
                width={isFullscreen ? 370 : 290}
              />
            ) : (
              <Button
                iconSrc={icons.register.loginIcon}
                text={t(isRequiresRegistrationOnly ? 'loginAndWatch' : 'login')}
                onClick={(e: LRUDEvent) => {
                  e.stopPropagation()
                  setAuthPopup({
                    sessionType: LoginSessionType.LOGIN,
                    prevFocusId: getParentContainerFocusId(
                      pathname,
                      !!pagePopup,
                    ),
                    redirectPath: pagePopup
                      ? `${paths.PAGE}/${pagePopup.id}${search}`
                      : '',
                    onSuccessHandler: pagePopup
                      ? undefined
                      : () => pageRefresh(search),
                  })
                  setMediaVideo(null)
                }}
                smallButton={!isFullscreen}
                width={isFullscreen ? 370 : 290}
              />
            )}
            {isFullscreen && (
              <BackBox>
                <IconButton
                  iconSrc={icons.navigation.backArrow}
                  onClick={() => {
                    sendEventToGTAG()
                    setIsFullscreen(false)
                    setMediaVideo(null)
                  }}
                  focusId={focusIdPrefix + 'section_2'}
                />
                <BodyText>{t('back')}</BodyText>
              </BackBox>
            )}
          </ButtonsBox>
        )}
      </ControlsContainer>
    </BlockedOverlayContainer>
  )
}

export const BlockedOverlay = memo(BlockedOverlayRaw)
